/**
 * Post-Trip Alert Summary Component
 * 
 * Displays a summary of alerts managed during a completed trip
 */

import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import {
  getTripAlertTracking,
  generatePostTripSummary,
  type AlertTracking,
} from '@/services/tripAlertTracking';
import type { Trip } from '@/services/trips';

type PostTripAlertSummaryProps = {
  trip: Trip;
};

export function PostTripAlertSummary({ trip }: PostTripAlertSummaryProps) {
  const theme = useTheme();
  const [tracking, setTracking] = useState<AlertTracking | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadTracking() {
      try {
        const data = await getTripAlertTracking(trip.id);
        if (mounted) {
          setTracking(data);
        }
      } catch (error) {
        console.error('[PostTripAlertSummary] Error loading tracking:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    loadTracking();

    return () => {
      mounted = false;
    };
  }, [trip.id]);

  if (loading) {
    return null;
  }

  const summary = generatePostTripSummary(tracking);

  return (
    <Card variant="outlined" style={{ marginTop: theme.spacing(2) }}>
      <View style={{ gap: theme.spacing(1.5) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1) }}>
          {summary.badge && (
            <View
              style={{
                paddingHorizontal: theme.spacing(1),
                paddingVertical: theme.spacing(0.5),
                borderRadius: theme.radii.pill,
                backgroundColor: theme.colors.success + '20',
              }}
            >
              <Caption color={theme.colors.success || '#34C759'} style={{ fontWeight: '600' }}>
                {summary.badge}
              </Caption>
            </View>
          )}
          <SubHeading style={{ fontSize: 16 }}>{summary.title}</SubHeading>
        </View>

        <Body color={theme.colors.textSecondary} style={{ fontSize: 13 }}>
          {summary.message}
        </Body>

        {summary.stats.totalAlerts > 0 && (
          <View
            style={{
              flexDirection: 'row',
              gap: theme.spacing(2),
              paddingTop: theme.spacing(1),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
            }}
          >
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
              <Ionicons
                name="notifications-outline"
                size={16}
                color={theme.colors.textSecondary}
              />
              <Caption color={theme.colors.textSecondary}>
                {summary.stats.totalAlerts} alert{summary.stats.totalAlerts > 1 ? 's' : ''}
              </Caption>
            </View>
            {summary.stats.criticalAlerts > 0 && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                <Ionicons name="warning-outline" size={16} color={theme.colors.error || '#FF3B30'} />
                <Caption color={theme.colors.error || '#FF3B30'}>
                  {summary.stats.criticalAlerts} critical
                </Caption>
              </View>
            )}
          </View>
        )}
      </View>
    </Card>
  );
}

